import Color from "color";
import { useMediaQuery } from "react-responsive";
import { useShallow } from "zustand/react/shallow";

import { useRoutesUiStore } from "@/components/RoutesProvider";
import getCompetitionRouteDisplay from "@/utils/getCompetitionRouteDisplay";

import { Route } from "../../core/types";
import { useOnDragStart } from "../../hooks/useOnDragStart";
import { MapPositionEdit } from "../RoutesProvider/RoutesProvider";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";

const emptySelection: Set<string> = new Set();

export const RoutePinText = ({
  route,
  fallback,
}: {
  route: Partial<Route> & { displayNumber?: string | number };
  fallback?: string;
  disableTagAlert?: boolean;
}) => {
  if (route.displayNumber) {
    return route.displayNumber.toString();
  }

  const inComp = !!route.competitionRouteGroups?.length && !route.circuit;

  if (inComp) {
    return getCompetitionRouteDisplay(route, {
      routeGroups: route.competitionRouteGroups!,
    }).number;
  }

  if (route.climbType === "BOULDER") {
    if (route.circuit?.routeDisplay === "NUMBER" && route.circuit?.number) {
      return route.circuit.number.toString();
    } else if (route.circuit?.routeDisplay === "GRADE" && route.pinGradeValue) {
      return route.pinGradeValue;
    } else if (route.circuit?.routeDisplay === "GRADE" && !route.gradeValue) {
      return fallback || null;
    } else if (!route.circuit && route.circuitNumber) {
      return route.circuitNumber.toString();
    }
  } else {
    if (route.pinGradeValue) {
      return route.pinGradeValue;
    } else {
      return fallback || null;
    }
  }

  // if (route.startLine && route.startLine.name) {
  //   return trimLineName(route.startLine.name, 3);
  // }

  return fallback || null;
};

export function MapRoutePinSvgRaw({
  route,
  onClick,
  opacity = 1,
  onContextMenu,
  dragToMoveEnabled = false,
  hideText = false,
  disableTagAlert = false,
  pinDiameter = 14,
  onDragStart,
}: {
  route: Partial<Route> & { displayNumber?: string };
  onClick?: React.MouseEventHandler<SVGGElement>;
  opacity?: number;
  onContextMenu?: React.MouseEventHandler<SVGGElement>;
  dragToMoveEnabled?: boolean;
  hideText?: boolean;
  disableTagAlert?: boolean;
  pinDiameter?: number;
  onDragStart?: (el: HTMLElement) => void;
}) {
  const routeColor = Color(route.colors?.split(":")?.[0] || "#FFFFFF");

  const hasTagWarning =
    route.climbType !== "SPORT" && route.startTime && !route.tag && !disableTagAlert;
  const strokeWidth = hasTagWarning ? 3 : 1;

  const isTouch = useMediaQuery({ query: "(pointer: coarse)" });

  return (
    <g
      transform="scale(1)"
      opacity={opacity}
      onClick={onClick}
      onContextMenu={onContextMenu}
      {...useOnDragStart(onDragStart ?? (() => {}))}
      style={{
        cursor: dragToMoveEnabled ? "move" : "pointer",
      }}
    >
      <circle
        cx={0}
        cy={0}
        r={pinDiameter / 2 + strokeWidth + (isTouch ? 8 : 4)}
        fill="transparent"
      />
      <rect
        x={-(pinDiameter + strokeWidth * 2) / 2}
        y={-(pinDiameter + strokeWidth * 2) / 2}
        width={pinDiameter + strokeWidth * 2}
        height={pinDiameter + strokeWidth * 2}
        rx={route.climbType === "SPORT" ? 6 : (pinDiameter + strokeWidth * 2) / 2}
        fill={hasTagWarning ? "#fce32a" : "black"}
        fillOpacity={hasTagWarning ? 1 : 0.1}
      />
      <rect
        x={-(pinDiameter / 2)}
        y={-(pinDiameter / 2)}
        width={pinDiameter}
        height={pinDiameter}
        rx={route.climbType === "SPORT" ? 3 : (pinDiameter + 2) / 2}
        fill={routeColor.toString()}
      />
      {/* <rect x="-5" y="-5" width="10" height="10" rx="10" fill={routeColor} /> */}
      {!hideText && (
        <text
          pointerEvents="none"
          x="0"
          y="1.2"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10px"
          fontWeight="bold"
          // letterSpacing={-0.5}
          fill={routeColor.isDark() ? "white" : "black"}
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          <RoutePinText route={route} disableTagAlert={disableTagAlert} />
        </text>
      )}
    </g>
  );
}

export default function MapRoutePinSvg({
  route,
  onClick,
  onContextMenu,
  dragToMoveEnabled = false,
  hideText = false,
  disableTagAlert = false,
  onDragStart,
}: {
  route: Partial<Route> & { displayNumber?: string };
  onClick?: React.MouseEventHandler<SVGGElement>;
  onContextMenu?: React.MouseEventHandler<SVGGElement>;
  dragToMoveEnabled?: boolean;
  hideText?: boolean;
  disableTagAlert?: boolean;
  onDragStart?: (el: HTMLElement) => void;
}) {
  // Also rendered in the schedule tab's route picker, which has no routes
  // table. Nothing is selected there, so the default onDragStart below stays
  // inert and callers that want dragging pass their own.
  const selectedRouteUuids =
    useRoutesTableStore((s) => s.selectedRouteUuids, { optional: true }) ?? emptySelection;

  const {
    currentlyDraggingRouteUuid,
    setCurrentlyDraggingRouteUuid,
    mapPositionEdits,
    setMapPositionEdit,
    routeFormEdits,
    createRouteData,
  } = useRoutesUiStore(
    useShallow((state) => ({
      currentlyDraggingRouteUuid: state.currentlyDraggingRouteUuid,
      setCurrentlyDraggingRouteUuid: state.setCurrentlyDraggingRouteUuid,
      mapPositionEdits: state.mapPositionEdits,
      setMapPositionEdit: state.setMapPositionEdit,
      routeFormEdits: state.routeFormEdits,
      createRouteData: state.createRouteData,
    })),
  );

  // hacky with createRouteData use here. is there a better way?
  const isSelected =
    selectedRouteUuids.has(route.uuid || "") || createRouteData?.uuid === route.uuid;
  const selectionCount = !createRouteData ? selectedRouteUuids.size : 1;

  const pinDiameter = 14;

  const opacity = dragToMoveEnabled && currentlyDraggingRouteUuid === route.uuid ? 0.2 : 1;

  const mapPositionEdit = mapPositionEdits[route.uuid || ""] as MapPositionEdit | undefined;

  const callerHandlesDrag = !!onDragStart;

  // TODO: Now that we are reusing this pin in the schedule tab, we should properly reduce the
  // coupling with the route ui store / selected route uuids.
  // For now, I'll just allow the caller to pass in onDragStart.
  if (!onDragStart) {
    onDragStart = () => {
      if (isSelected && selectionCount === 1) {
        setCurrentlyDraggingRouteUuid(route.uuid || "");
        if (route.climbType === "SPORT") {
          setMapPositionEdit(route.uuid || "", {
            ...(route.mapPosition ? route.mapPosition : {}),
            map: route.map,
            ...mapPositionEdit,
          } as MapPositionEdit);
        }
      }
    };
  }

  route = { ...route, ...(route.uuid && routeFormEdits[route.uuid]) };
  // return (
  //   <g
  //     transform="scale(1)"
  //     opacity={opacity}
  //     onClick={onClick}
  //     onContextMenu={onContextMenu}
  //     {...(dragToMoveEnabled ? dragCallbacks : {})}
  //     style={{
  //       cursor: dragToMoveEnabled ? "move" : "pointer",
  //     }}
  //   >
  //     <circle
  //       cx={0}
  //       cy={0}
  //       r={pinDiameter / 2 + strokeWidth + (isTouch ? 8 : 4)}
  //       fill="transparent"
  //     />
  //     <rect
  //       x={-(pinDiameter + strokeWidth * 2) / 2}
  //       y={-(pinDiameter + strokeWidth * 2) / 2}
  //       width={pinDiameter + strokeWidth * 2}
  //       height={pinDiameter + strokeWidth * 2}
  //       rx={
  //         route.climbType === "SPORT" ? 6 : (pinDiameter + strokeWidth * 2) / 2
  //       }
  //       fill={hasTagWarning ? "#fce32a" : "black"}
  //       fillOpacity={hasTagWarning ? 1 : 0.1}
  //     />
  //     <rect
  //       x={-(pinDiameter / 2)}
  //       y={-(pinDiameter / 2)}
  //       width={pinDiameter}
  //       height={pinDiameter}
  //       rx={route.climbType === "SPORT" ? 3 : (pinDiameter + 2) / 2}
  //       fill={routeColor.toString()}
  //     />
  //     {/* <rect x="-5" y="-5" width="10" height="10" rx="10" fill={routeColor} /> */}
  //     {!hideText && (
  //       <text
  //         pointerEvents="none"
  //         x="0"
  //         y="1.2"
  //         textAnchor="middle"
  //         dominantBaseline="middle"
  //         fontSize="10px"
  //         fontWeight="bold"
  //         // letterSpacing={-0.5}
  //         fill={routeColor.isDark() ? "white" : "black"}
  //         style={{
  //           userSelect: "none",
  //           WebkitUserSelect: "none",
  //           MozUserSelect: "none",
  //           msUserSelect: "none",
  //         }}
  //       >
  //         <RoutePinText route={route} disableTagAlert={disableTagAlert} />
  //       </text>
  //     )}
  //   </g>
  // );

  // Only offer the move cursor when a drag would actually do something: either
  // the caller handles it, or the default handler's own guard is satisfied.
  const canDragToMove =
    dragToMoveEnabled && (callerHandlesDrag || (isSelected && selectionCount === 1));

  return (
    <MapRoutePinSvgRaw
      route={route}
      onClick={onClick}
      opacity={opacity}
      onContextMenu={onContextMenu}
      dragToMoveEnabled={canDragToMove}
      hideText={hideText}
      disableTagAlert={disableTagAlert}
      pinDiameter={pinDiameter}
      onDragStart={onDragStart}
    />
  );
}

export const MapRoutePinDraggingSvg = ({
  route,
}: {
  route: Partial<Route> & { displayNumber?: string | number };
}) => {
  const pinDiameter = 14;
  const totalWidth = pinDiameter + 4;
  const finalWidth = 28;

  const routeColor = Color(route.colors?.split(":")?.[0] || "#FFFFFF");
  return (
    <svg
      width={finalWidth}
      height={finalWidth}
      viewBox={`0 0 ${totalWidth} ${totalWidth}`}
      style={{ cursor: "move" }}
    >
      <g transform={`translate(${totalWidth / 2}, ${totalWidth / 2})`}>
        <rect
          x={-pinDiameter / 2}
          y={-pinDiameter / 2}
          width={pinDiameter}
          height={pinDiameter}
          rx={route.climbType === "BOULDER" ? pinDiameter / 2 : 3}
          stroke={routeColor.darken(0.1).toString()}
          strokeWidth="2"
        />
        {/* <rect x="-5" y="-5" width="10" height="10" rx="10" fill={routeColor} /> */}
        <rect
          x={-pinDiameter / 2}
          y={-pinDiameter / 2}
          width={pinDiameter}
          height={pinDiameter}
          rx={route.climbType === "BOULDER" ? pinDiameter / 2 : 3}
          fill={routeColor.toString()}
        />
        <text
          x="0"
          y="1.2"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10px"
          fontWeight="bold"
          // letterSpacing={-0.5}
          fill={routeColor.isDark() ? "white" : "black"}
        >
          <RoutePinText route={route} />
        </text>
      </g>
    </svg>
  );
};

export const MapRoutePinUnplacedSvg = ({
  route,
  onClick,
  onContextMenu,
}: {
  route: Partial<Route> & { uuid: string; displayNumber?: number };
  onClick?: React.MouseEventHandler<SVGGElement>;
  onContextMenu?: React.MouseEventHandler<SVGGElement>;
}) => {
  const selectedRouteUuids = useRoutesTableStore((s) => s.selectedRouteUuids);

  const {
    currentlyDraggingRouteUuid,
    setCurrentlyDraggingRouteUuid,
    mapPositionEdits,
    setMapPositionEdit,
    routeFormEdits,
  } = useRoutesUiStore(
    useShallow((state) => ({
      currentlyDraggingRouteUuid: state.currentlyDraggingRouteUuid,
      setCurrentlyDraggingRouteUuid: state.setCurrentlyDraggingRouteUuid,
      mapPositionEdits: state.mapPositionEdits,
      setMapPositionEdit: state.setMapPositionEdit,
      routeFormEdits: state.routeFormEdits,
    })),
  );

  const isSelected = selectedRouteUuids.has(route.uuid);
  const isCurrentlyDragging = currentlyDraggingRouteUuid === route.uuid;
  const mapPositionEdit = mapPositionEdits[route.uuid] as MapPositionEdit | undefined;

  const dragCallbacks = useOnDragStart(() => {
    setCurrentlyDraggingRouteUuid(route.uuid);
    setMapPositionEdit(route.uuid, {
      ...(route.mapPosition ? route.mapPosition : {}),
      map: route.map,
      ...mapPositionEdit,
    } as MapPositionEdit);
  });

  const pinDiameter = 14;
  const totalWidth = pinDiameter + 4;
  const finalWidth = 24;
  route = { ...route, ...routeFormEdits[route.uuid] };
  const routeColor = Color(route.colors?.split(":")?.[0] || "#FFFFFF");
  return (
    <svg width={finalWidth} height={finalWidth} viewBox={`0 0 ${totalWidth} ${totalWidth}`}>
      <g
        transform={`translate(${totalWidth / 2}, ${totalWidth / 2})`}
        onClick={onClick}
        {...dragCallbacks}
        onContextMenu={onContextMenu}
        style={{
          cursor: "move",
        }}
        opacity={isCurrentlyDragging ? 0.25 : isSelected || selectedRouteUuids.size === 0 ? 1 : 0.5}
      >
        <rect
          x={-pinDiameter / 2}
          y={-pinDiameter / 2}
          width={pinDiameter}
          height={pinDiameter}
          rx={route.climbType === "BOULDER" ? pinDiameter / 2 : 3}
          stroke={routeColor.darken(0.1).toString()}
          strokeWidth="2"
        />
        {/* <rect x="-5" y="-5" width="10" height="10" rx="10" fill={routeColor} /> */}
        <rect
          x={-pinDiameter / 2}
          y={-pinDiameter / 2}
          width={pinDiameter}
          height={pinDiameter}
          rx={route.climbType === "BOULDER" ? pinDiameter / 2 : 3}
          fill={routeColor.toString()}
        />
        <text
          x="0"
          y="1.2"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10px"
          fontWeight="bold"
          // letterSpacing={-0.5}
          fill={routeColor.isDark() ? "white" : "black"}
        >
          <RoutePinText route={route} fallback="?" />
        </text>
      </g>
    </svg>
  );
};
